package com.arenaproplay.admin.ui.components.charts.svg

/**
 * Shared data models for the Night Arena SVG chart kit (R2).
 * Colors are passed as "#RRGGBB" hex strings; callers use AppColors.*.toHexString().
 */

data class ChartSeries(
    val name: String,
    val values: List<Double>,
    /** "#RRGGBB" */
    val colorHex: String,
)

data class ChartAnnotation(
    /** 0-based index into labels/values where the annotation sits. */
    val index: Int,
    val text: String,
    /** "#RRGGBB" */
    val colorHex: String = "#E4CD40",
)

data class DonutSlice(
    val name: String,
    val value: Double,
    /** "#RRGGBB" */
    val colorHex: String,
)

data class FunnelStep(
    val label: String,
    val value: Long,
    /** "#RRGGBB" or null → uses Primary gradient */
    val colorHex: String? = null,
    /** Override text color (null = auto-contrast) */
    val textColorHex: String? = null,
)

data class RankedRow(
    val label: String,
    val value: String,
    /** 0..1 fill fraction */
    val fraction: Double,
)

data class BarBucket(
    val label: String,
    val value: Double,
)

// ── Internal SVG helpers shared across chart builders ──────────────────────

internal fun fmt(v: Double): String {
    if (v.isNaN() || v.isInfinite()) return "0"
    val r = kotlin.math.round(v * 100.0) / 100.0
    val i = r.toInt()
    return if (r == i.toDouble()) "$i" else "$r"
}

internal fun xmlEsc(s: String) =
    s.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;")

/**
 * Catmull-Rom spline → cubic Bezier path string.
 * Port of the technique used in kontaq's SimpleTabs.kt / PlatformCharts.kt.
 */
internal fun smoothPath(px: DoubleArray, py: DoubleArray): String {
    val n = px.size
    if (n == 0) return ""
    if (n == 1) return "M ${fmt(px[0])} ${fmt(py[0])}"
    val sb = StringBuilder("M ${fmt(px[0])} ${fmt(py[0])}")
    for (i in 0 until n - 1) {
        val i0 = maxOf(i - 1, 0)
        val i3 = minOf(i + 2, n - 1)
        val c1x = px[i] + (px[i + 1] - px[i0]) / 6.0
        val c1y = py[i] + (py[i + 1] - py[i0]) / 6.0
        val c2x = px[i + 1] - (px[i3] - px[i]) / 6.0
        val c2y = py[i + 1] - (py[i3] - py[i]) / 6.0
        sb.append(" C ${fmt(c1x)} ${fmt(c1y)}, ${fmt(c2x)} ${fmt(c2y)}, ${fmt(px[i + 1])} ${fmt(py[i + 1])}")
    }
    return sb.toString()
}
